import EventEmitter from "@folio-2026/shared/eventemitter"
import { primaryInput } from "detect-it"

import Experience from "../index"

export interface PointerCoords {
  /**
   * Real X (pixels)
   */
  x: number
  /**
   * Real Y (pixels)
   */
  y: number
  /**
   * Normalized X ([-1, 1] range, center is (0, 0))
   */
  ndcX: number
  /**
   * Normalized Y ([-1, 1] range, center is (0, 0))
   */
  ndcY: number

  /**
   * Normalized X (0..1 range, center is (0.5, 0.5))
   */
  normalizedX: number
  /**
   * Normalized Y (0..1 range, center is (0.5, 0.5))
   */
  normalizedY: number
}

export default class Pointer extends EventEmitter {
  private sizes: Experience["sizes"]
  private onMouseMoveHandler: ((event: MouseEvent) => void) | null = null
  private onFirstGestureHandler: ((event: Event) => void) | null = null
  private onDeviceMotionHandler: ((event: DeviceMotionEvent) => void) | null =
    null
  private isEnablingDeviceMotion = false
  private hasEnabledDeviceMotion = false

  private hasMotionBaseline = false
  private motionBaseX = 0
  private motionBaseY = 0
  private motionSmoothedNormalizedX = 0.5
  private motionSmoothedNormalizedY = 0.5

  private readonly motionSensitivity = 8
  private readonly motionSmoothingAlpha = 0.08

  /**
   * Real X (pixels)
   **/
  x: number = 0
  /**
   * Real Y (pixels)
   **/
  y: number = 0

  /**
   * Normalized X ([-1, 1] range, center is (0, 0))
   **/
  ndcX: number = 0
  /**
   * Normalized Y ([-1, 1] range, center is (0, 0))
   **/
  ndcY: number = 0

  /**
   * Normalized X (0..1 range, center is (0.5, 0.5))
   */
  normalizedX: number = 0.5
  /**
   * Normalized Y (0..1 range, center is (0.5, 0.5))
   */
  normalizedY: number = 0.5

  primaryInput: "mouse" | "touch" = primaryInput

  on(event: "move", callback: (coords: PointerCoords) => void): this | false
  on(names: string, callback: (...args: unknown[]) => void): this | false
  on(
    names: string,
    callback:
      | ((coords: PointerCoords) => void)
      | ((...args: unknown[]) => void),
  ): this | false {
    return super.on(names, callback as (...args: unknown[]) => void)
  }

  constructor() {
    super()

    const experience = Experience.getInstance()
    this.sizes = experience.sizes

    this.setupEventListeners()
  }

  private setupEventListeners() {
    if (this.primaryInput === "mouse") {
      this.onMouseMoveHandler = this.onMouseMove.bind(this)
      window.addEventListener("mousemove", this.onMouseMoveHandler)
      return
    }

    this.setupDeviceMotion()
  }

  private onMouseMove(event: MouseEvent) {
    const width = this.sizes.width
    const height = this.sizes.height

    if (width <= 0 || height <= 0) return

    const normalizedX = event.clientX / width
    const normalizedY = event.clientY / height

    this.setFromNormalized({ normalizedX, normalizedY })
  }

  private setupDeviceMotion() {
    this.onFirstGestureHandler = this.onFirstGesture.bind(this)

    window.addEventListener("pointerdown", this.onFirstGestureHandler, {
      once: true,
      passive: true,
    })
    window.addEventListener("touchstart", this.onFirstGestureHandler, {
      once: true,
      passive: true,
    })

    // Ensure we start from center until motion becomes available
    this.setFromNormalized({
      normalizedX: 0.5,
      normalizedY: 0.5,
      shouldEmit: false,
    })
  }

  private async onFirstGesture(_event: Event) {
    if (this.hasEnabledDeviceMotion || this.isEnablingDeviceMotion) return

    await enableSensors()
    void this.enableDeviceMotion()
  }

  private async enableDeviceMotion() {
    if (this.isEnablingDeviceMotion) return

    this.isEnablingDeviceMotion = true

    const isGranted = await this.requestDeviceMotionPermission()
    if (!isGranted) {
      this.isEnablingDeviceMotion = false
      return
    }

    this.hasEnabledDeviceMotion = true
    this.isEnablingDeviceMotion = false
    this.hasMotionBaseline = false
    this.startDeviceMotion()
  }

  private async requestDeviceMotionPermission() {
    const DeviceMotionEventConstructor = getDeviceMotionEventConstructor()
    if (!DeviceMotionEventConstructor) return false

    if (typeof DeviceMotionEventConstructor.requestPermission !== "function")
      return true

    try {
      const result = await DeviceMotionEventConstructor.requestPermission()
      return result === "granted"
    } catch {
      return false
    }
  }

  private startDeviceMotion() {
    if (this.onDeviceMotionHandler) return

    this.onDeviceMotionHandler = this.onDeviceMotion.bind(this)

    window.addEventListener("devicemotion", this.onDeviceMotionHandler, {
      passive: true,
    })
  }

  private onDeviceMotion(event: DeviceMotionEvent) {
    const motion = event.accelerationIncludingGravity
    if (!motion) return

    const ax = motion.x
    const ay = motion.y

    if (typeof ax !== "number" || typeof ay !== "number") return

    if (!this.hasMotionBaseline) {
      this.motionBaseX = ax
      this.motionBaseY = ay
      this.motionSmoothedNormalizedX = 0.5
      this.motionSmoothedNormalizedY = 0.5
      this.hasMotionBaseline = true

      this.setFromNormalized({
        normalizedX: 0.5,
        normalizedY: 0.5,
        shouldEmit: false,
      })
      return
    }

    const dx = ax - this.motionBaseX
    const dy = ay - this.motionBaseY

    const angle = getScreenAngle()
    const rotated = rotateByScreenAngle({ x: dx, y: dy, angle })

    const targetNormalizedX = 0.5 + rotated.x / this.motionSensitivity
    const targetNormalizedY = 0.5 + rotated.y / this.motionSensitivity

    this.motionSmoothedNormalizedX = lerp(
      this.motionSmoothedNormalizedX,
      targetNormalizedX,
      this.motionSmoothingAlpha,
    )
    this.motionSmoothedNormalizedY = lerp(
      this.motionSmoothedNormalizedY,
      targetNormalizedY,
      this.motionSmoothingAlpha,
    )

    this.setFromNormalized({
      normalizedX: this.motionSmoothedNormalizedX,
      normalizedY: this.motionSmoothedNormalizedY,
    })
  }

  private setFromNormalized({
    normalizedX,
    normalizedY,
    shouldEmit = true,
  }: {
    normalizedX: number
    normalizedY: number
    shouldEmit?: boolean
  }) {
    const width = this.sizes.width
    const height = this.sizes.height

    if (width <= 0 || height <= 0) return

    const clampedNormalizedX = clamp01(normalizedX)
    const clampedNormalizedY = clamp01(normalizedY)

    this.normalizedX = clampedNormalizedX
    this.normalizedY = clampedNormalizedY

    this.x = clampedNormalizedX * width
    this.y = clampedNormalizedY * height

    // Normalize coordinates to [-1, 1] range
    this.ndcX = clampedNormalizedX * 2 - 1
    this.ndcY = -(clampedNormalizedY * 2 - 1) // Invert Y axis

    if (!shouldEmit) return

    this.trigger("move", [
      {
        x: this.x,
        y: this.y,
        ndcX: this.ndcX,
        ndcY: this.ndcY,
        normalizedX: this.normalizedX,
        normalizedY: this.normalizedY,
      },
    ])
  }

  destroy() {
    if (this.onMouseMoveHandler) {
      window.removeEventListener("mousemove", this.onMouseMoveHandler)
      this.onMouseMoveHandler = null
    }

    if (this.onFirstGestureHandler) {
      window.removeEventListener("pointerdown", this.onFirstGestureHandler)
      window.removeEventListener("touchstart", this.onFirstGestureHandler)
      this.onFirstGestureHandler = null
    }

    if (this.onDeviceMotionHandler) {
      window.removeEventListener("devicemotion", this.onDeviceMotionHandler)
      this.onDeviceMotionHandler = null
    }
  }
}

function clamp01(value: number) {
  if (Number.isNaN(value)) return 0.5
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

function lerp(current: number, target: number, alpha: number) {
  return current + (target - current) * alpha
}

function getScreenAngle() {
  const rawAngle =
    typeof screen !== "undefined" && screen.orientation?.angle != null
      ? screen.orientation.angle
      : typeof (window as unknown as LegacyOrientationWindow).orientation ===
          "number"
        ? (window as unknown as LegacyOrientationWindow).orientation
        : 0

  const normalized = normalizeAngleDegrees(rawAngle)
  const snapped = Math.round(normalized / 90) * 90

  return normalizeAngleDegrees(snapped)
}

function normalizeAngleDegrees(angle: number) {
  const normalized = angle % 360
  if (normalized < 0) return normalized + 360
  return normalized
}

function rotateByScreenAngle({
  x,
  y,
  angle,
}: {
  x: number
  y: number
  angle: number
}) {
  if (angle === 90) return { x: y, y: -x }
  if (angle === 180) return { x: -x, y: -y }
  if (angle === 270) return { x: -y, y: x }

  return { x, y }
}

type DeviceMotionPermissionState = "granted" | "denied"

type DeviceMotionEventConstructorWithPermission = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<DeviceMotionPermissionState>
}

type LegacyOrientationWindow = Window & {
  orientation?: number
}

function getDeviceMotionEventConstructor(): DeviceMotionEventConstructorWithPermission | null {
  if (typeof window === "undefined") return null
  if (!("DeviceMotionEvent" in window)) return null

  return window.DeviceMotionEvent as unknown as DeviceMotionEventConstructorWithPermission
}

async function enableSensors() {
  // Try the modern way first
  try {
    const AccelerometerConstructor = getAccelerometerConstructor()
    if (!AccelerometerConstructor) {
      attachLegacyMotionProbe()
      return
    }

    const sensor = new AccelerometerConstructor({ frequency: 60 })
    sensor.start()
  } catch (_error) {
    attachLegacyMotionProbe()
  }
}

function attachLegacyMotionProbe() {
  // Attempting to listen to the event often forces Chrome to recognize the intent
  window.addEventListener(
    "devicemotion",
    (event) => {
      const x = event.acceleration?.x
      void x
    },
    { once: true, passive: true },
  )
}

type AccelerometerLike = {
  start: () => void
}

type AccelerometerConstructor = new (options: {
  frequency?: number
}) => AccelerometerLike

function getAccelerometerConstructor(): AccelerometerConstructor | null {
  const globalWithAccelerometer = globalThis as unknown as {
    Accelerometer?: AccelerometerConstructor
  }

  const AccelerometerConstructor = globalWithAccelerometer.Accelerometer
  if (!AccelerometerConstructor) return null

  return AccelerometerConstructor
}
